import { TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Box, CircularProgress, Checkbox, Tooltip, IconButton, Stack } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PreviewIcon from '@mui/icons-material/Preview';
import { useNavigate } from "react-router-dom";
import { Domains } from "../../data/Domains";
import VillageConsoleContext from "../../store/VillageConsoleContext";
import { useVillageRules } from "../../hooks/useVillageRules";
import { VillageRulesDto } from "../../dtos/VillageRulesDto";


type DataRow = {
    
    dateFrom: string,
    dateTo: string,
    isThirdParty: boolean,
    version: string,
    villageRuleId: number

};


const compareRows = ( first: DataRow, second: DataRow ) => {

    if ( first.dateFrom > second.dateFrom ) {

        return -1;

    }

    if ( first.dateFrom < second.dateFrom ) {

        return 1;

    }

    return 0;

};


const formatData = ( villageRule: VillageRulesDto ): DataRow => {

    const dateFrom = new Date( villageRule.startDateTime ).toLocaleDateString("en-AU");
    const dateTo = villageRule.endDateTime.toString()[0] === '0' ? '' : new Date(villageRule.endDateTime).toLocaleDateString("en-AU");
    const version = villageRule.endDateTime.toString()[0] === '0' ? villageRule.version + ' (current)' : villageRule.version;

    return { 
        
        dateFrom, 
        dateTo,
        isThirdParty: villageRule.isThirdParty,
        version, 
        villageRuleId: villageRule.villageRuleId 
        
    };

};


export const SearchRules: React.FC = ( ) => {

    const [ loadingKey, setLoadingKey ] = useState< number >( 0 );
    const [ rows, setRows ] = useState< DataRow[ ] >( [ ] );

    const { domain } = useContext( VillageConsoleContext );
    const { getVillageRule, getVillageRules } = useVillageRules( );
    const navigate = useNavigate( );


    useEffect(() => { initialize( ); }, [ ] )
    useEffect(() => { initialize( ); }, [ domain ] )


    const downloadPDF = ( pdf: string, row: DataRow ) => {
        
        const linkSource = `data:application/pdf;base64,${pdf}`;
        const downloadLink = document.createElement( 'a' );
        const fileName = `VillageRules-${row.version}.pdf`;
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click( );
        setLoadingKey( 0 );

    }


    const handleDownloadPdfClick = async ( row: DataRow ) => {

        setLoadingKey( row.villageRuleId );

        const villageRule = await getVillageRule( domain as Domains, row.villageRuleId );

        if( villageRule ) {

            downloadPDF( villageRule.rulePdf, row );

        }

    };


    const handleViewVillageRulesClick = ( villageRuleId: number ) => navigate( `/view-rules/${villageRuleId}` );


    const initialize = async( ) => {

        setRows( [ ] );
        
        const villageRules: VillageRulesDto[ ] = await getVillageRules( domain );

        const formattedRows: DataRow[] = [];

        for( const villageRule of villageRules ){

            formattedRows.push( formatData( villageRule ) );

        }
    
        setRows( formattedRows.sort( compareRows ) );

    }; 

    return (

        <Stack direction="column" display="flex" justifyContent="center" gap={ 3 } sx={{ marginTop: '30px' }}>
        
            <TableContainer component={ Paper } sx={{ alignSelf: "center", boxShadow: 5, maxWidth: 'fit-content', overflowY: 'hidden' }}>

                <Table sx={{ minWidth: 650 }}>
                    
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Version</TableCell>
                             { domain === Domains.Bma && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Is Third Party</TableCell>  }
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Date From</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Date To</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        
                        {
                            rows.length === 0
                                &&
                            <TableRow>
                                <TableCell align="center" colSpan={ 6 } >
                                    <Box 
                                        alignItems="center"
                                        display="flex" 
                                        justifyContent="center"
                                    >
                                        <CircularProgress size={ 40 } sx={{ color: '#e65400' }}/>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        }

                        {   rows.map( ( row ) => (
                                <TableRow key={ row.villageRuleId } sx={{ '&:last-child td, &:last-child th': { border: 0 } }} hover>

                                    <TableCell align="center">{row.version}</TableCell>
                                    { domain === Domains.Bma && <TableCell align="center"> <Checkbox disabled checked={ row.isThirdParty } /> </TableCell> }
                                    <TableCell align="center">{row.dateFrom}</TableCell>
                                    <TableCell align="center">{row.dateTo}</TableCell>
                                    <TableCell align="center">

                                        <Tooltip title={ `View Village Rules ${row.version}` }>
                                            <IconButton aria-label="view" onClick={() => handleViewVillageRulesClick( row.villageRuleId )} disabled={ loadingKey !== 0 }>
                                                <PreviewIcon/>
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title={ `Download PDF for ${row.version}` }>
                                            <IconButton aria-label="download pdf" onClick={() => handleDownloadPdfClick( row )} disabled={ loadingKey !== 0 } >
                                                <PictureAsPdfIcon/>
                                                
                                                { 
                                                    ( loadingKey === row.villageRuleId ) 
                                                        && 
                                                    <CircularProgress
                                                        size={55}
                                                            sx={{
                                                            position: 'absolute',
                                                            top: 3,
                                                            left: 3,
                                                            zIndex: 1,
                                                            marginTop: '-10px',
                                                            marginLeft: '-10px',
                                                        }}
                                                    />
                                                }
                                            </IconButton>
                                        </Tooltip>

                                    </TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>

                </Table>

            </TableContainer>
        
        </Stack>

    );
    
};